import React, {
  useContext,
  useState,
  useEffect,
  useRef,
  useCallback,
} from "react";
import {
  Box,
  TextField,
  Tab,
  Typography,
  Stack,
  Grid,
  FormHelperText,
} from "@mui/material";
import { Edit as EditIcon, Delete as DeleteIcon } from "@mui/icons-material";
import { TabContext, TabList, TabPanel } from "@mui/lab";
import { AppContext } from "../context";
import { pathJoinUrl } from "@smfa/utils/src";
import {
  getFiestaBanner,
  updateFiestaBanner,
  deleteFiestaSession,
  getFiestaIntro,
  updateFiestaIntro,
  getFiestaSession,
  addEditFiestaSession,
} from "../actions/fiestasession.actions";
import DeleteModal from "../ui-components/DeleteModal";
import dayjs from "dayjs";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import {
  LocalizationProvider,
  MobileDateTimePicker,
} from "@mui/x-date-pickers";
import RichTextEditor from "../components/RichTextEditor";
import LimitedTextField from "../components/common/LimitedTextField";
import FileUploadArea from "../components/common/FileUploadArea";
import FormActionBar from "../components/common/FormActionBar";
import DataTable from "../components/common/DataTable";
import SearchControls from "../components/common/SearchControls";
import TablePagination from "../components/common/TablePagination";
import {
  isValidEmbedVideo,
  isValidUrl,
} from "@smfa/utils/src/common/functions";
import ToolBody from "../components/common/ToolBody";

// Default fiesta session date settings
const FIESTA_SESSION_DEFAULTS = {
  SESSION_DATE: {
    DAYS_FROM_TODAY: 30,
    HOUR: 14, // 2:00 PM
    MINUTE: 0,
  },
};

/**
 * Generate default session date (30 days from today at 2:00 PM)
 * @returns {Object} dayjs object with the default date/time
 */
const getDefaultSessionDate = () => {
  const now = new Date();
  now.setDate(
    now.getDate() + FIESTA_SESSION_DEFAULTS.SESSION_DATE.DAYS_FROM_TODAY,
  );
  now.setHours(
    FIESTA_SESSION_DEFAULTS.SESSION_DATE.HOUR,
    FIESTA_SESSION_DEFAULTS.SESSION_DATE.MINUTE,
    0,
    0,
  );
  return dayjs(now);
};

/**
 * Admin: Fiesta Sessions
 * @returns {JSX.element}
 * @component
 */
const FiestaSessions = () => {
  // Basic state
  const [activeTab, setValue] = useState("1");
  const { storage } = useContext(AppContext);
  const [loading, setLoading] = useState(false);
  const [errorMsg, setErrorMsg] = useState("");
  const [successMsg, setSuccessMsg] = useState("");
  const editorContentRef = useRef("");

  // Session editor state
  const [submitting, setSubmitting] = useState(false);
  const [imageFile, setImageFile] = useState(null);
  const imageFileInputRef = useRef(null);
  const [imagePreviewUrl, setImagePreviewUrl] = useState("");

  useEffect(() => {
    if (!imageFile) {
      setImagePreviewUrl("");

      return undefined;
    }

    const previewUrl = URL.createObjectURL(imageFile);
    setImagePreviewUrl(previewUrl);

    return () => {
      URL.revokeObjectURL(previewUrl);
    };
  }, [imageFile]);

  // Banner defaults
  const addAboutBanner_default = {
    fiesta_banner_section_id: 0,
    banner_image: "",
    banner_image_name: "",
    banner_image_error: false,
    banner_image_mob: "",
    banner_image_mob_name: "",
    banner_image_mob_error: false,
    banner_title: "",
    banner_title_maxlenth: 30,
    banner_title_error: false,
  };

  // Intro defaults
  const fiesta_intro_default = {
    fiesta_intro_section_id: 0,
    section_title: "",
    section_title_maxlenth: 255,
    section_title_error: false,
    section_description: "",
    section_description_maxlenth: 65535,
    section_description_error: false,
  };

  // Form defaults
  const formFields = {
    session_id: 0,
    session_title: "",
    session_title_maxlenth: 40,
    session_title_error: false,
    session_dt: getDefaultSessionDate(),
    session_dt_error: false,
    session_content: "",
    session_content_error: false,
    session_url: "",
    session_url_error: false,
    session_video_url: "",
    session_video_url_error: false,
    session_image: "",
    session_image_error: "",
    session_image_display: "",
  };

  // Form states
  const [fiestaIntro, setFiestaIntro] = useState(fiesta_intro_default);
  const [addAboutBanner, setAboutBanner] = useState(addAboutBanner_default);
  const [formValues, setFormValues] = useState(formFields);

  // Image preview states
  const [deskimage, setDeskImage] = useState("");
  const [mobimage, setMobImage] = useState("");

  // Table data states
  const [sessionList, setSessionList] = useState([]);
  const [firstLoading, setFirstLoading] = useState(true);
  const [pageNo, setPageNo] = useState(1);
  const [perPage, setPerPage] = useState(50);
  const [totalCount, setTotalCount] = useState(0);
  const [totalPage, setTotalPage] = useState(1);
  const [searchText, setSearchText] = useState("");
  const [searchType, setSearchType] = useState("session_title");
  const [pageLoader, setPageLoader] = useState(false);
  const [deleteItem, setDeleteItem] = useState(null);

  // Message handlers
  const setMessage = (success, error) => {
    setSuccessMsg(success);
    setErrorMsg(error);
  };

  const clearMessages = () => {
    setMessage("", "");
  };

  // Tab change handler
  const handleChange = useCallback((event, newValue) => {
    clearMessages();
    setLoading(false);
    setValue(newValue);

    // Reset form with fresh defaults when switching to add session tab
    const freshFormFields = {
      ...formFields,
      session_dt: getDefaultSessionDate(),
    };
    setFormValues(freshFormFields);

    // Load session list when switching to manage tab
    if (newValue === "4") {
      setFirstLoading(true);
      loadFiestaSessions();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Initial data load
  useEffect(() => {
    // Load banner data
    getFiestaBanner().then((res) => {
      if (res.data.status === true) {
        const bannerData = { ...addAboutBanner };
        bannerData.fiesta_banner_section_id =
          res.data.data.fiesta_banner_section_id;
        // Ensure null values are converted to empty strings
        bannerData.banner_title = res.data.data.banner_title || "";
        bannerData.banner_image_name = res.data.data.banner_image;
        bannerData.banner_image_mob_name = res.data.data.banner_image_mob;

        setAboutBanner(bannerData);

        // Set image previews
        if (res.data.data.banner_image !== null) {
          setDeskImage(
            pathJoinUrl(storage[0].imageEndUrl, res.data.data.banner_image),
          );
        }
        if (res.data.data.banner_image_mob !== null) {
          setMobImage(
            pathJoinUrl(storage[0].imageEndUrl, res.data.data.banner_image_mob),
          );
        }
      }
    });

    // Load intro data
    getFiestaIntro().then((res) => {
      if (res.data.status === true) {
        const introData = { ...fiestaIntro };
        introData.fiesta_intro_section_id =
          res.data.data.fiesta_intro_section_id;
        // Ensure null values are converted to empty strings
        introData.section_title = res.data.data.section_title || "";
        introData.section_description = res.data.data.section_description || "";

        setFiestaIntro(introData);
      }
    });

    // Load sessions list
    loadFiestaSessions();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Load sessions data
  const loadFiestaSessions = useCallback(() => {
    setFirstLoading(true);

    getFiestaSession({
      pageNo: pageNo,
      perPage: perPage,
      searchText: searchText,
      searchType: searchType,
    }).then((res) => {
      if (res.data.status) {
        setTotalCount(res.data.pagination.totalCount);
        setTotalPage(res.data.pagination.totalPage);
        setSessionList(res.data.data);
      } else {
        setSessionList([]);
        setTotalCount(0);
        setTotalPage(1);
      }
      setFirstLoading(false);
    });
  }, [pageNo, perPage, searchText, searchType]);

  // Banner field change handler
  const addAboutBannerInfo = (event) => {
    clearMessages();

    // eslint-disable-next-line no-unused-vars
    const { name, value } = event.target;
    const bannerData = { ...addAboutBanner };

    if (name === "banner_title") {
      if (value.length <= addAboutBanner.banner_title_maxlenth) {
        bannerData.banner_title = value;
        bannerData.banner_title_error = false;
      }
    }

    setAboutBanner(bannerData);
  };

  // Banner image change handlers
  const onImageChange = (event) => {
    clearMessages();

    if (event.target.files[0]) {
      const file = event.target.files[0];
      setDeskImage(URL.createObjectURL(file));

      const bannerData = { ...addAboutBanner };
      bannerData.banner_image = file;
      bannerData.banner_image_name = file.name;
      bannerData.banner_image_error = false;

      setAboutBanner(bannerData);
    }
  };

  const onMobImageChange = (event) => {
    clearMessages();

    if (event.target.files && event.target.files[0]) {
      const file = event.target.files[0];
      setMobImage(URL.createObjectURL(file));

      const bannerData = { ...addAboutBanner };
      bannerData.banner_image_mob = file;
      bannerData.banner_image_mob_name = file.name;
      bannerData.banner_image_mob_error = false;

      setAboutBanner(bannerData);
    }
  };

  const clearBannerUpload = (field, clearPreview) => {
    setAboutBanner((prev) => ({
      ...prev,
      [field]: "",
      [`${field}_name`]: "",
    }));
    clearPreview("");
  };

  // Banner form submit
  const submitLocation = () => {
    setLoading(true);
    clearMessages();

    // Validate banner title
    if (addAboutBanner.banner_title === "") {
      document.getElementById("banner_title").focus();

      const bannerData = { ...addAboutBanner };
      bannerData.banner_title_error = true;
      setAboutBanner(bannerData);

      setLoading(false);
      return false;
    }

    // Prepare form data
    const formData = new FormData();
    formData.append(
      "fiesta_banner_section_id",
      addAboutBanner.fiesta_banner_section_id,
    );
    formData.append("banner_image_mob", addAboutBanner.banner_image_mob);
    formData.append("banner_image", addAboutBanner.banner_image);
    // Ensure we send empty strings instead of null values
    formData.append("banner_title", addAboutBanner.banner_title || "");

    // Submit based on whether it's a new record or update
    if (addAboutBanner.fiesta_banner_section_id === 0) {
      updateFiestaBanner(formData)
        .then((res) => {
          if (res.data.status === true) {
            setMessage("Banner added successfully!", "");
            setLoading(false);
            setTimeout(() => setMessage("", ""), 3000);
          } else {
            setAboutBanner(addAboutBanner_default);
            setMessage("", res.data.message);
            setLoading(false);
          }
        })
        .catch(() => {
          setAboutBanner(addAboutBanner_default);
          setLoading(false);
          setMessage("", "Something went wrong, please try again!");
        });
    } else {
      updateFiestaBanner(formData)
        .then((res) => {
          if (res.data.status === true) {
            setMessage("Banner Updated Successfully!", "");
            setLoading(false);
            setTimeout(() => setMessage("", ""), 3000);
          } else {
            setMessage("", "Something went wrong, please try again!");
            setLoading(false);
          }
        })
        .catch(() => {
          setLoading(false);
          setMessage("", "Something went wrong, please try again!");
        });
    }
  };

  // Intro field change handler
  const handleChangeIntro = (event) => {
    clearMessages();

    const { name, value } = event.target;
    const introData = { ...fiestaIntro };

    if (name === "cat_section_title") {
      if (value.length <= fiestaIntro.section_title_maxlenth) {
        introData.section_title = value;
        introData.section_title_error = false;
      }
    } else if (name === "cat_section_description") {
      if (value.length <= fiestaIntro.section_description_maxlenth) {
        introData.section_description = value;
        introData.section_description_error = false;
      }
    }

    setFiestaIntro(introData);
  };

  // Intro form submit
  const submitFiestaIntro = () => {
    setLoading(true);
    clearMessages();

    const introData = { ...fiestaIntro };
    let hasError = false;

    // Validate required fields
    // if (fiestaIntro.section_title === "") {
    //   introData.section_title_error = true;
    //   hasError = true;
    // }

    if (fiestaIntro.section_description === "") {
      introData.section_description_error = true;
      hasError = true;
    }

    if (hasError) {
      setFiestaIntro(introData);
      setLoading(false);
      return false;
    }

    // Prepare form data
    const formData = new FormData();
    formData.append(
      "fiesta_intro_section_id",
      fiestaIntro.fiesta_intro_section_id,
    );
    // Ensure we send empty strings instead of null values
    formData.append("section_title", fiestaIntro.section_title || "");
    formData.append(
      "section_description",
      fiestaIntro.section_description || "",
    );

    // Submit based on whether it's a new record or update
    if (fiestaIntro.fiesta_intro_section_id === 0) {
      updateFiestaIntro(formData)
        .then((res) => {
          if (res.data.status === true) {
            setMessage("Introduction added successfully!", "");
            setLoading(false);
            setTimeout(() => setMessage("", ""), 3000);
          } else {
            setFiestaIntro(fiesta_intro_default);
            setMessage("", res.data.message);
            setLoading(false);
          }
        })
        .catch(() => {
          setFiestaIntro(fiesta_intro_default);
          setLoading(false);
          setMessage("", "Something went wrong, please try again!");
        });
    } else {
      updateFiestaIntro(formData)
        .then((res) => {
          if (res.data.status === true) {
            setMessage("Introduction Updated Successfully!", "");
            setLoading(false);
            setTimeout(() => setMessage("", ""), 3000);
          } else {
            setMessage("", "Something went wrong, please try again!");
            setLoading(false);
          }
        })
        .catch(() => {
          setLoading(false);
          setMessage("", "Something went wrong, please try again!");
        });
    }
  };

  // Table pagination handlers
  const handlePerPageChange = useCallback(
    (value) => {
      setPerPage(value);
      setPageNo(1);

      getFiestaSession({
        pageNo: 1,
        perPage: value,
        searchText: searchText,
        searchType: searchType,
      }).then((res) => {
        setTotalCount(res.data.pagination.totalCount);
        setTotalPage(res.data.pagination.totalPage);
        setSessionList(res.data.data.length > 0 ? res.data.data : []);
      });
    },
    [searchText, searchType],
  );

  const handlePageChange = useCallback(
    (page) => {
      if (page < 1) page = 1;
      if (page > totalPage) page = totalPage;

      setPageLoader(true);
      setPageNo(page);

      getFiestaSession({
        pageNo: page,
        perPage: perPage,
        searchText: searchText,
        searchType: searchType,
      }).then((res) => {
        setTotalCount(res.data.pagination.totalCount);
        setTotalPage(res.data.pagination.totalPage);
        setSessionList(res.data.data.length > 0 ? res.data.data : []);
        setPageLoader(false);
      });
    },
    [perPage, searchText, searchType, totalPage],
  );

  // Session form change handlers
  const handleFormChange = (event) => {
    clearMessages();

    const { name, value } = event.target;
    const sessionData = { ...formValues };

    if (name === "session_title") {
      if (value.length <= formValues.session_title_maxlenth) {
        sessionData.session_title = value;
        sessionData.session_title_error = false;
      }
    } else if (name === "session_url") {
      sessionData.session_url = value;
      sessionData.session_url_error = false;
    } else if (name === "session_video_url") {
      sessionData.session_video_url = value;
      sessionData.session_video_url_error = false;
    }

    setFormValues(sessionData);
  };

  // Rich text editor handler - uses ref approach to avoid cursor jumping
  const handleContentChange = (event, editor) => {
    clearMessages();
    // Update ref instead of state to avoid re-renders during typing
    editorContentRef.current = editor.getData();

    // Only update error state if needed
    if (formValues.session_content_error) {
      setFormValues({
        ...formValues,
        session_content_error: false,
      });
    }
  };

  // Date picker handler
  const handleSessionDateTime = (dateTime) => {
    setFormValues({
      ...formValues,
      session_dt: dateTime,
      session_dt_error: false,
    });
  };

  // Session edit handler
  const handleEdit = useCallback((e, val) => {
    clearMessages();
    setLoading(false);
    handleImageFileRemove();

    // Update the editor ref with content
    editorContentRef.current = val.session_content || "";

    setFormValues({
      ...formFields,
      session_id: val.session_id,
      session_title: val.session_title,
      session_dt: val.session_dt ? dayjs(val.session_dt) : null,
      session_content: val.session_content || "",
      session_url: val.session_url || "",
      session_video_url: val.session_video_url || "",
      session_image: val.session_image || "",
      session_image_display: val.session_image_display || "",
    });

    setValue("3");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Table sort handlers
  const handleSort = useCallback(
    (field, direction) => {
      setErrorMsg("");
      let element;

      if (field === "session_title") {
        if (direction === "desc") {
          element = document.getElementById("session_titleasc");
          element.classList.remove("sortopacity");
          element.style.visibility = "inherit";
          element = document.getElementById("session_titledesc");
          element.classList.add("sortopacity");
          element.style.visibility = "hidden";
        } else {
          element = document.getElementById("session_titledesc");
          element.classList.remove("sortopacity");
          element.style.visibility = "inherit";
          element = document.getElementById("session_titleasc");
          element.classList.add("sortopacity");
          element.style.visibility = "hidden";
        }
      }

      setFirstLoading(true);
      getFiestaSession({
        pageNo: pageNo,
        perPage: perPage,
        searchText: searchText,
        searchType: searchType,
        sort: direction,
        sortvalue: field,
      }).then((res) => {
        setTotalCount(res.data.pagination.totalCount);
        setTotalPage(res.data.pagination.totalPage);
        setSessionList(res.data.data.length > 0 ? res.data.data : []);
        setFirstLoading(false);
      });
    },
    [pageNo, perPage, searchText, searchType],
  );

  // Search handlers
  const handleSearchTextChange = (value) => {
    setSearchText(value);
  };

  const handleSearchTypeChange = (value) => {
    setSearchType(value);
  };

  const handleSearch = () => {
    setErrorMsg("");

    if (searchType === "session_title" && searchText === "") {
      return false;
    }

    setFirstLoading(true);
    getFiestaSession({
      pageNo: 1,
      perPage: perPage,
      searchText: searchText,
      searchType: searchType,
    }).then((res) => {
      setTotalCount(res.data.pagination.totalCount);
      setTotalPage(res.data.pagination.totalPage);
      setSessionList(res.data.data.length > 0 ? res.data.data : []);
      setFirstLoading(false);
    });
  };

  const clearSearch = () => {
    setErrorMsg("");
    setSearchText("");
    setSearchType("session_title");
    setFirstLoading(true);

    getFiestaSession({
      pageNo: 1,
      perPage: perPage,
      searchText: "",
      searchType: "session_title",
    }).then((res) => {
      setTotalCount(res.data.pagination.totalCount);
      setTotalPage(res.data.pagination.totalPage);
      setSessionList(res.data.data.length > 0 ? res.data.data : []);
      setFirstLoading(false);
    });
  };

  // Form reset handler
  const resetForm = () => {
    if (submitting) {
      return;
    }
    clearMessages();
    setLoading(false);
    // Create fresh form fields with new default date
    const freshFormFields = {
      ...formFields,
      session_dt: getDefaultSessionDate(),
    };
    setFormValues(freshFormFields);
    editorContentRef.current = "";
    handleImageFileRemove();

    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  // Delete handlers
  const handleDelete = (item) => {
    setDeleteItem(item);
  };

  const handleDeleteSuccess = () => {
    setDeleteItem(null);
    loadFiestaSessions();
  };

  // Form submission
  const handleSubmit = (e) => {
    e.preventDefault();

    if (submitting) return;
    if (!formValidate()) return;

    setSubmitting(true);
    formSubmit();
  };

  // Image file handlers
  const handleImageFileRemove = () => {
    setImageFile(null);
    if (imageFileInputRef.current) {
      imageFileInputRef.current.value = "";
    }
    setFormValues((prev) => ({
      ...prev,
      session_image: prev.session_id ? prev.session_image : "",
    }));
  };

  const clearSessionUpload = () => {
    setImageFile(null);
    setFormValues((prev) => ({
      ...prev,
      session_image: "",
      session_image_display: "",
    }));
    setImagePreviewUrl("");
    if (imageFileInputRef.current) {
      imageFileInputRef.current.value = "";
    }
  };

  const handleImageFileChange = (event) => {
    let files = Array.from(event.target.files);
    files = files.filter((file) => file.name.match(/\.(png|jpg|jpeg)$/i));
    if (files.length) {
      setImageFile(files[0]);
      setFormValues({ ...formValues, session_image_error: "" });
    }
  };

  // Session form submit
  const formSubmit = () => {
    let formData = new FormData();
    formData.append("session_id", formValues.session_id);
    formData.append("session_title", formValues.session_title);
    formData.append(
      "session_dt",
      formValues.session_dt
        ? dayjs(formValues.session_dt).format("YYYY-MM-DD HH:mm:ss")
        : "",
    );

    // Get content from ref instead of state to avoid cursor jumping issues
    formData.append("session_content", editorContentRef.current);

    formData.append("session_url", formValues.session_url);
    formData.append("session_video_url", formValues.session_video_url);

    if (imageFile) {
      formData.append("session_image", imageFile);
    }

    addEditFiestaSession(formData)
      .then((res) => {
        if (res.data.status) {
          setSuccessMsg(res.data.message);
          resetForm();
          loadFiestaSessions();
          setTimeout(() => {
            setSuccessMsg("");
            setValue("4");
          }, 2000);
        } else {
          setErrorMsg(
            res.data.data || res.data.message || "An error occurred.",
          );
        }
        setSubmitting(false);
      })
      .catch((error) => {
        const errorData = error.response?.data;
        let errorMessage = "Something went wrong, please try again!";
        if (errorData?.message) {
          errorMessage = errorData.message;
        }
        if (errorData?.errors) {
          const firstError = Object.values(errorData.errors)[0];
          errorMessage = firstError[0] || errorMessage;
        }
        setErrorMsg(errorMessage);
        setSubmitting(false);
      });
  };

  // Form validation
  const formValidate = () => {
    let formValid = true;
    let sessionData = { ...formValues };

    // Reset all error states
    sessionData.session_title_error = false;
    sessionData.session_dt_error = false;
    sessionData.session_content_error = false;
    sessionData.session_url_error = false;
    sessionData.session_video_url_error = false;
    sessionData.session_image_error = false;

    // Validate title
    if (formValues.session_title.trim() === "") {
      formValid = false;
      sessionData.session_title_error = true;
    }

    // Validate date & time
    if (!formValues.session_dt || !dayjs(formValues.session_dt).isValid()) {
      formValid = false;
      sessionData.session_dt_error = true;
    }

    if (formValues.session_url && !isValidUrl(formValues.session_url)) {
      formValid = false;
      sessionData.session_url_error = true;
    }

    if (
      formValues.session_video_url &&
      !isValidUrl(formValues.session_video_url) &&
      !isValidEmbedVideo(formValues.session_video_url)
    ) {
      formValid = false;
      sessionData.session_video_url_error = true;
    }

    setFormValues(sessionData);
    return formValid;
  };

  // Define table columns for Sessions
  const tableColumns = [
    {
      field: "index",
      label: "ID",
      width: "10%",
      render: (row, index) => pageNo * perPage - perPage + 1 + index,
    },
    {
      field: "session_title",
      label: "Title",
      width: "40%",
      sortable: true,
    },
    {
      field: "session_dt_display",
      label: "Date & Time",
      width: "35%",
      sortable: true,
    },
    {
      field: "actions",
      label: "Actions",
      width: "15%",
      render: (row) => (
        <Stack direction="row" spacing={1}>
          <EditIcon
            onClick={(e) => handleEdit(e, row)}
            style={{ cursor: "pointer", marginRight: "10px" }}
          />
          <DeleteIcon
            onClick={() => handleDelete(row)}
            className="del-admn-sec"
            style={{ cursor: "pointer" }}
          />
        </Stack>
      ),
    },
  ];

  // Define search options
  const searchOptions = [{ value: "session_title", label: "Title" }];

  return (
    <ToolBody>
      <TabContext value={activeTab}>
        <Box sx={{ borderBottom: 1, borderColor: "divider" }}>
          <TabList onChange={handleChange} aria-label="fiesta sessions tabs">
            <Tab label="Add Banner" value="1" />
            <Tab label="Add Introduction" value="2" />
            <Tab
              label={`${
                formValues.session_id !== 0 ? "Update" : "Add"
              } Fiesta Session`}
              value="3"
            />
            <Tab label="Manage Fiesta Sessions" value="4" />
          </TabList>
        </Box>

        {/* Tab 1: Banner */}
        <TabPanel value="1">
          <Stack spacing={3}>
            {/* Banner Title */}
            <Box sx={{ maxWidth: 400 }}>
              <LimitedTextField
                id="banner_title"
                name="banner_title"
                label="Title"
                value={addAboutBanner.banner_title}
                onChange={addAboutBannerInfo}
                maxLength={addAboutBanner.banner_title_maxlenth}
                error={addAboutBanner.banner_title_error}
                errorText="Please enter a Title!"
              />
            </Box>

            {/* Banner Images */}
            <Grid container spacing={3}>
              <Grid size={{ xs: 12, md: 6 }}>
                <FileUploadArea
                  title="Add Banner Image For Desktop"
                  description={
                    <>
                      Best image Resolution: 1920px X 836px
                      <br />
                      Supported files types: JPG, JPEG & PNG
                    </>
                  }
                  previewUrl={deskimage}
                  fileName={addAboutBanner.banner_image_name}
                  onChange={onImageChange}
                  deleteRequest={{
                    resource: "fiesta-banner",
                    id: addAboutBanner.fiesta_banner_section_id,
                    field: "banner_image",
                  }}
                  onRemove={() =>
                    clearBannerUpload("banner_image", setDeskImage)
                  }
                  accept=".png,.jpg,.jpeg"
                />
              </Grid>
              <Grid size={{ xs: 12, md: 6 }}>
                <FileUploadArea
                  title="Add Banner Image For Mobile"
                  description={
                    <>
                      Best image Resolution: 400px X 350px
                      <br />
                      Supported files types: JPG, JPEG & PNG
                    </>
                  }
                  previewUrl={mobimage}
                  fileName={addAboutBanner.banner_image_mob_name}
                  onChange={onMobImageChange}
                  deleteRequest={{
                    resource: "fiesta-banner",
                    id: addAboutBanner.fiesta_banner_section_id,
                    field: "banner_image_mob",
                  }}
                  onRemove={() =>
                    clearBannerUpload("banner_image_mob", setMobImage)
                  }
                  accept=".png,.jpg,.jpeg"
                />
              </Grid>
            </Grid>

            {/* Form Actions */}
            <FormActionBar
              onSubmit={submitLocation}
              isSubmitting={loading}
              isEdit={true}
              successMessage={successMsg}
              errorMessage={errorMsg}
              submitLabel="Update"
            />
          </Stack>
        </TabPanel>

        {/* Tab 2: Introduction */}
        <TabPanel value="2">
          <Stack spacing={3}>
            {/* Intro Title & Description */}
            <Grid container spacing={3}>
              <Grid size={{ xs: 12, md: 4 }}>
                <LimitedTextField
                  id="standard-basic"
                  name="cat_section_title"
                  label="Title"
                  value={fiestaIntro.section_title}
                  onChange={handleChangeIntro}
                  maxLength={fiestaIntro.section_title_maxlenth}
                  error={fiestaIntro.section_title_error}
                  errorText="Please enter Title!"
                  variant="standard"
                />
              </Grid>
            </Grid>

            <Grid container spacing={3}>
              <Grid size={{ xs: 12 }}>
                <LimitedTextField
                  id="standard-textarea"
                  name="cat_section_description"
                  label="Short Description"
                  value={fiestaIntro.section_description}
                  onChange={handleChangeIntro}
                  error={fiestaIntro.section_description_error}
                  errorText="Please enter Short Description!"
                  multiline
                  rows={10}
                  variant="standard"
                />
              </Grid>
            </Grid>

            {/* Form Actions */}
            <FormActionBar
              onSubmit={submitFiestaIntro}
              isSubmitting={loading}
              isEdit={true}
              successMessage={successMsg}
              errorMessage={errorMsg}
              submitLabel="Update"
            />
          </Stack>
        </TabPanel>

        {/* Tab 3: Add/Edit Fiesta Session */}
        <TabPanel value="3">
          <Stack spacing={3}>
            {/* Session Title & DateTime */}
            <Grid container spacing={3}>
              <Grid size={{ xs: 12, sm: 6 }}>
                <TextField
                  id="session_title"
                  label="Title *"
                  variant="standard"
                  onChange={handleFormChange}
                  name="session_title"
                  value={formValues.session_title}
                  error={formValues.session_title_error}
                  helperText={
                    formValues.session_title_error
                      ? "Please enter Title!"
                      : `Max Characters: ${formValues.session_title_maxlenth - formValues.session_title.length}`
                  }
                  slotProps={{
                    htmlInput: {
                      maxLength: formValues.session_title_maxlenth,
                    },
                  }}
                  fullWidth
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <LocalizationProvider dateAdapter={AdapterDayjs}>
                  <MobileDateTimePicker
                    label="Session Date & Time *"
                    value={formValues.session_dt}
                    onChange={handleSessionDateTime}
                    slotProps={{
                      textField: {
                        variant: "standard",
                        fullWidth: true,
                        error: formValues.session_dt_error,
                        helperText: formValues.session_dt_error
                          ? "Please select Session Date & Time"
                          : "",
                      },
                    }}
                  />
                </LocalizationProvider>
              </Grid>
            </Grid>

            {/* Session Content */}
            <Box>
              <Typography variant="caption" color="text.secondary">
                Session Content
              </Typography>
              <RichTextEditor
                data={
                  formValues.session_id
                    ? formValues.session_content
                    : editorContentRef.current
                }
                onChange={handleContentChange}
              />
              {formValues.session_content_error && (
                <FormHelperText error>
                  Please enter session content.
                </FormHelperText>
              )}
            </Box>

            {/* Session URLs */}
            <Grid container spacing={3}>
              <Grid size={12}>
                <TextField
                  id="session_url"
                  label="Session URL (for online meeting)"
                  variant="standard"
                  fullWidth
                  onChange={handleFormChange}
                  name="session_url"
                  value={formValues.session_url}
                  error={formValues.session_url_error}
                  helperText={
                    formValues.session_url_error
                      ? "Please enter a valid URL (e.g., https://example.com)"
                      : ""
                  }
                />
              </Grid>
              <Grid size={12}>
                <TextField
                  id="session_video_url"
                  label="Session Video URL or embed code (for archive)"
                  variant="standard"
                  fullWidth
                  onChange={handleFormChange}
                  name="session_video_url"
                  value={formValues.session_video_url}
                  error={formValues.session_video_url_error}
                  helperText={
                    formValues.session_video_url_error
                      ? "Please enter a valid URL (e.g., https://youtube.com/...) or embed code"
                      : ""
                  }
                  multiline
                  rows={4}
                />
              </Grid>
            </Grid>

            {/* Session Image */}
            <Grid container spacing={3}>
              <Grid size={{ xs: 12, sm: 6 }}>
                <FileUploadArea
                  title="Add Session Thumbnail"
                  description="Supported files types: JPG, JPEG & PNG"
                  previewUrl={
                    imagePreviewUrl
                      ? imagePreviewUrl
                      : formValues.session_id &&
                          formValues.session_image_display
                        ? formValues.session_image_display
                        : null
                  }
                  fileName={
                    imageFile ? imageFile.name : formValues.session_image
                  }
                  onChange={handleImageFileChange}
                  deleteRequest={{
                    resource: "fiesta-session",
                    id: formValues.session_id,
                    field: "session_image",
                  }}
                  onDeleteSuccess={clearSessionUpload}
                  accept=".png,.jpg,.jpeg"
                  error={formValues.session_image_error}
                  onRemove={handleImageFileRemove}
                  showRemove={!!imageFile}
                  fileInputRef={imageFileInputRef}
                />
              </Grid>
            </Grid>

            {/* Form Actions */}
            <FormActionBar
              onSubmit={handleSubmit}
              onReset={resetForm}
              isSubmitting={submitting}
              isEdit={formValues.session_id !== 0}
              successMessage={successMsg}
              errorMessage={errorMsg}
              submitLabel={formValues.session_id !== 0 ? "Update" : "Save"}
              resetLabel={
                formValues.session_id !== 0 ? "Change to ADD" : "Reset"
              }
            />
          </Stack>
        </TabPanel>

        {/* Tab 4: Manage Fiesta Sessions */}
        <TabPanel value="4">
          {/* Search Controls */}
          <SearchControls
            searchText={searchText}
            searchType={searchType}
            searchOptions={searchOptions}
            onSearchChange={handleSearchTextChange}
            onTypeChange={handleSearchTypeChange}
            onSubmit={handleSearch}
            onClear={clearSearch}
            perPage={perPage}
            totalCount={totalCount}
            onPerPageChange={handlePerPageChange}
          />

          {/* Data Table */}
          <DataTable
            columns={tableColumns}
            data={sessionList}
            loading={firstLoading}
            sortConfig={{ field: null, direction: null }}
            onSort={handleSort}
            keyField="session_id"
            noDataMessage="No Fiesta Sessions Found"
          />

          {/* Pagination */}
          {totalPage > 1 && !firstLoading && (
            <TablePagination
              page={pageNo}
              totalPages={totalPage}
              loading={pageLoader}
              onChange={handlePageChange}
              entriesPerPage={perPage}
              onEntriesChange={handlePerPageChange}
            />
          )}
        </TabPanel>
      </TabContext>
      {/* Delete Confirmation Modal */}
      <DeleteModal
        open={deleteItem !== null}
        title="Delete Fiesta Session"
        message="Are you sure you want to delete this Fiesta Session?"
        handleClose={() => setDeleteItem(null)}
        handleSuccess={handleDeleteSuccess}
        deleteAction={deleteFiestaSession}
        data={deleteItem ? { session_id: deleteItem.session_id } : null}
      />
    </ToolBody>
  );
};

export default FiestaSessions;
